import type { AddToCartSource, ProductSourceType } from './sources';

export interface ProductListDescriptor {
  id: string;
  name: string;
  /** Surface the list is rendered as; becomes `source` on cart events */
  source: ProductSourceType;
  /** Catalogue category the list is, when it is one; fills `item_category` on snippets */
  category?: string;
}

export const categoryProductList = (
  id: string | number,
  name: string,
): ProductListDescriptor => ({
  id: String(id),
  name,
  source: 'category_page',
  category: name,
});

/** Page the CMS category block is rendered on; the same block appears on all */
export type CategoryBlockSurface = 'home' | 'search' | 'shop';

/** Shape the CMS category block renders its products in */
export type CategoryBlockLayout = 'carousel' | 'grid';

const BLOCK_SURFACE_LABELS: Record<CategoryBlockSurface, string> = {
  home: 'Home',
  search: 'Search',
  shop: 'Shop',
};

const BLOCK_LAYOUT_SOURCES: Record<CategoryBlockLayout, ProductSourceType> = {
  carousel: 'home_carousel',
  grid: 'home_grid',
};

export const categoryBlockProductList = (
  id: string | number,
  name: string,
  surface: CategoryBlockSurface,
  layout: CategoryBlockLayout,
): ProductListDescriptor => ({
  id: `${surface}:${id}`,
  name: `${BLOCK_SURFACE_LABELS[surface]}: ${name}`,
  source: BLOCK_LAYOUT_SOURCES[layout],
});

export const searchProductList = (): ProductListDescriptor => ({
  id: 'search',
  name: 'Search results',
  source: 'search',
});

/**
 * Search suggest in the header. Carries its own id so BI can tell it from the
 * results page, which reports the same `search` type.
 */
export const searchSuggestProductList = (): ProductListDescriptor => ({
  id: 'search_suggest',
  name: 'Search suggest',
  source: 'search',
});

export const favoritesProductList = (): ProductListDescriptor => ({
  id: 'favorites',
  name: 'Favorites',
  source: 'favorites',
});

/** Items already in the cart, whose quantity is changed there */
export const cartProductList = (): ProductListDescriptor => ({
  id: 'cart',
  name: 'Cart',
  source: 'cart',
});

export const recommendationsProductList = (
  source: string,
  name: string,
): ProductListDescriptor => ({
  id: `recommendations:${source}`,
  name: `Recommendations: ${name}`,
  source: 'recommendations',
});

/**
 * Cart attribution for a card rendered inside `list`. Outside any list — the
 * product page and the modals it opens — the product page is the surface.
 */
export const toAddToCartSource = (
  list: ProductListDescriptor | null,
): AddToCartSource =>
  list
    ? { type: list.source, id: list.id, name: list.name }
    : { type: 'product_page' };
